// src/pages/SportsPage.jsx - Mobile dropdown league filter + Desktop sidebar

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SportsSidebar from '../components/sports/SportsSidebar';
import MatchCard from '../components/sports/MatchCard';
import { useBetSlip } from '../context/BetSlipContext';
import { FiFilter, FiGrid, FiList, FiRefreshCw, FiCalendar, FiChevronDown, FiChevronLeft, FiChevronRight, FiMenu } from 'react-icons/fi';
import { FootballAPI } from '../services/footballApi';
import { BasketballAPI } from '../services/basketballApi';

const SportsPage = () => {
  const navigate = useNavigate();
  const [selectedSport, setSelectedSport] = useState('football');
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [displayedMatches, setDisplayedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [displayCount, setDisplayCount] = useState(20);
  const [activeDateFilter, setActiveDateFilter] = useState('all');
  const [showLeagueFilter, setShowLeagueFilter] = useState(false);
  const [availableLeagues, setAvailableLeagues] = useState([]);
  const { betslipCount } = useBetSlip();
  
  const loadMoreRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Sports configuration
  const sports = [
    { id: 'football', name: 'Football', icon: '⚽', color: 'from-green-600 to-green-800' },
    { id: 'basketball', name: 'Basketball', icon: '🏀', color: 'from-orange-600 to-orange-800' },
    { id: 'tennis', name: 'Tennis', icon: '🎾', color: 'from-yellow-600 to-yellow-800' },
    { id: 'baseball', name: 'Baseball', icon: '⚾', color: 'from-blue-600 to-blue-800' },
  ];

  // League priority function
  const getLeaguePriority = useCallback((leagueId, leagueName, sport = 'football') => {
    if (sport === 'football') {
      const priorityMap = { 1: 1, 2: 2, 39: 3, 140: 4, 135: 5, 78: 6, 61: 7 };
      if (priorityMap[leagueId]) return priorityMap[leagueId];
      const name = leagueName?.toLowerCase() || '';
      if (name.includes('fifa world cup') || name.includes('world cup')) return 1;
      if (name.includes('champions league')) return 2;
      if (name.includes('premier league')) return 3;
      if (name.includes('la liga')) return 4;
      if (name.includes('serie a')) return 5;
      if (name.includes('bundesliga')) return 6;
      if (name.includes('ligue 1')) return 7;
      return 999;
    } else if (sport === 'basketball') {
      const priorityMap = { 13: 1, 12: 2, 178: 3, 14: 4, 3: 5, 4: 6, 5: 7, 6: 8, 7: 9, 8: 10 };
      if (priorityMap[leagueId]) return priorityMap[leagueId];
      const name = leagueName?.toLowerCase() || '';
      if (name.includes('wnba') || name.includes('nba w')) return 1;
      if (name.includes('nba')) return 2;
      if (name.includes('big3')) return 3;
      if (name.includes('nit')) return 4;
      if (name.includes('liga acb')) return 5;
      return 999;
    }
    return 999;
  }, []);

  // Group fixtures by league
  const groupFixturesByLeague = useCallback((fixtures) => {
    if (!Array.isArray(fixtures) || fixtures.length === 0) return [];

    const grouped = {};
    fixtures.forEach(fixture => {
      const leagueId = fixture.league_id;
      if (!grouped[leagueId]) {
        grouped[leagueId] = {
          league_id: leagueId,
          league_name: fixture.league_name || `League ${leagueId}`,
          league_logo: fixture.league_logo || null,
          country: fixture.country || null,
          fixtures: []
        };
      }
      grouped[leagueId].fixtures.push(fixture);
    });

    const result = Object.values(grouped);
    result.sort((a, b) => {
      const priorityA = getLeaguePriority(a.league_id, a.league_name, selectedSport);
      const priorityB = getLeaguePriority(b.league_id, b.league_name, selectedSport);
      return priorityA - priorityB;
    });

    return result;
  }, [getLeaguePriority, selectedSport]);

  const flattenMatches = useCallback((grouped) => {
    const flattened = [];
    if (!Array.isArray(grouped)) return flattened;
    
    grouped.forEach(group => {
      if (group.fixtures && Array.isArray(group.fixtures)) {
        group.fixtures.forEach(match => {
          if (match) {
            flattened.push({
              ...match,
              _league_id: group.league_id,
              _league_name: group.league_name,
              _league_logo: group.league_logo,
              _league_country: group.country
            });
          }
        });
      }
    });
    
    flattened.sort((a, b) => {
      const dateA = new Date(a.fixture_date || a.date);
      const dateB = new Date(b.fixture_date || b.date);
      return dateA - dateB;
    });
    
    return flattened;
  }, []);

  const getDisplayedMatches = useCallback((allMatches, count) => {
    return allMatches.slice(0, count);
  }, []);

  const loadMoreMatches = useCallback(() => {
    const allMatches = flattenMatches(groupedMatches);
    const currentCount = displayCount;
    const nextCount = Math.min(currentCount + 20, allMatches.length);
    
    if (nextCount > currentCount) {
      setLoadingMore(true);
      setTimeout(() => {
        setDisplayCount(nextCount);
        const newDisplayed = getDisplayedMatches(allMatches, nextCount);
        setDisplayedMatches(newDisplayed);
        setLoadingMore(false);
        setHasMore(nextCount < allMatches.length);
      }, 300);
    }
  }, [groupedMatches, displayCount, flattenMatches, getDisplayedMatches]);

  // Fetch basketball data
  const fetchBasketballData = async () => {
    setLoading(true);
    setError(null);
    setDisplayCount(20);
    setHasMore(true);
    
    try {
      let response;
      if (selectedDate) {
        response = await BasketballAPI.getGames(selectedDate);
      } else {
        response = await BasketballAPI.getUpcomingGames(100);
      }
      
      if (response.error) throw new Error(response.error);
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const firstItem = response.data[0];
        let allGrouped;
        
        if (firstItem.fixtures && Array.isArray(firstItem.fixtures)) {
          allGrouped = response.data;
        } else {
          allGrouped = groupFixturesByLeague(response.data);
        }
        
        if (selectedLeague) {
          allGrouped = allGrouped.filter(group => group.league_id === selectedLeague);
        }
        
        setGroupedMatches(allGrouped);
        
        const allMatches = flattenMatches(allGrouped);
        const initialDisplay = getDisplayedMatches(allMatches, 20);
        setDisplayedMatches(initialDisplay);
        setHasMore(allMatches.length > 20);
        setLastUpdate(new Date());
      } else {
        setGroupedMatches([]);
        setDisplayedMatches([]);
        setHasMore(false);
        setError('No upcoming basketball matches found');
      }
    } catch (error) {
      // console.error('Basketball fetch error:', error);
      setError('Failed to load basketball matches. Please try again.');
      setGroupedMatches([]);
      setDisplayedMatches([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch football data
  const fetchFootballData = async () => {
    setLoading(true);
    setError(null);
    setDisplayCount(20);
    setHasMore(true);
    
    try {
      let response;
      if (selectedDate) {
        response = await FootballAPI.getFixtures(selectedDate);
      } else {
        response = await FootballAPI.getUpcoming(100);
      }
      
      if (response.error) throw new Error(response.error);
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        let allGrouped = response.data;
        const firstItem = response.data[0];
        
        if (!firstItem.fixtures) {
          const grouped = {};
          response.data.forEach(fixture => {
            const leagueId = fixture.league_id;
            if (!grouped[leagueId]) {
              grouped[leagueId] = {
                league_id: leagueId,
                league_name: fixture.league_name || `League ${leagueId}`,
                league_logo: fixture.league_logo || null,
                country: fixture.country || null,
                fixtures: []
              };
            }
            grouped[leagueId].fixtures.push(fixture);
          });
          allGrouped = Object.values(grouped);
        }
        
        allGrouped.sort((a, b) => {
          const priorityA = getLeaguePriority(a.league_id, a.league_name, 'football');
          const priorityB = getLeaguePriority(b.league_id, b.league_name, 'football');
          return priorityA - priorityB;
        });
        
        if (selectedLeague) {
          allGrouped = allGrouped.filter(group => group.league_id === selectedLeague);
        }
        
        setGroupedMatches(allGrouped);
        
        const allMatches = flattenMatches(allGrouped);
        const initialDisplay = getDisplayedMatches(allMatches, 20);
        setDisplayedMatches(initialDisplay);
        setHasMore(allMatches.length > 20);
        setLastUpdate(new Date());
      } else {
        setGroupedMatches([]);
        setDisplayedMatches([]);
        setHasMore(false);
        setError('No upcoming football matches found');
      }
    } catch (error) {
      // console.error('Football fetch error:', error);
      setError('Failed to load football matches. Please try again.');
      setGroupedMatches([]);
      setDisplayedMatches([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data based on selected sport
  const fetchData = useCallback(() => {
    if (selectedSport === 'football') {
      fetchFootballData();
    } else if (selectedSport === 'basketball') {
      fetchBasketballData();
    } else {
      setGroupedMatches([]);
      setDisplayedMatches([]);
      setLoading(false);
      setHasMore(false);
      setError(`${selectedSport.charAt(0).toUpperCase() + selectedSport.slice(1)} integration coming soon`);
    }
  }, [selectedSport]);

  // Manual refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  // Date filter handlers
  const handleDateFilter = (filter) => {
    setActiveDateFilter(filter);
    if (filter === 'all') {
      setSelectedDate(null);
    } else if (filter === 'today') {
      const today = new Date();
      setSelectedDate(today.toISOString().split('T')[0]);
    } else if (filter === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setSelectedDate(tomorrow.toISOString().split('T')[0]);
    } else {
      setSelectedDate(filter);
    }
  };

  // League select handler
  const handleLeagueSelect = (leagueId) => {
    setSelectedLeague(leagueId === selectedLeague ? null : leagueId);
    setShowLeagueFilter(false);
  };

  // Match click handler
  const handleMatchClick = (matchId) => {
    navigate(`/match/${matchId}`);
  };

  // Scroll sport selector horizontally
  const scrollSports = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 120;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreMatches();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [loading, loadingMore, hasMore, loadMoreMatches]);

  // Update displayed matches
  useEffect(() => {
    if (groupedMatches.length > 0) {
      const allMatches = flattenMatches(groupedMatches);
      const newDisplayed = getDisplayedMatches(allMatches, displayCount);
      setDisplayedMatches(newDisplayed);
      setHasMore(displayCount < allMatches.length);
    }
  }, [groupedMatches, displayCount, flattenMatches, getDisplayedMatches]);

  // Initial load and re-fetch
  useEffect(() => {
    fetchData();
  }, [selectedSport]);

  useEffect(() => {
    if (selectedDate !== undefined) {
      fetchData();
    }
  }, [selectedDate, selectedLeague]);

  // Extract unique leagues for filter
  useEffect(() => {
    if (groupedMatches.length > 0) {
      const leagues = groupedMatches.map(g => ({
        id: g.league_id,
        name: g.league_name,
        logo: g.league_logo
      }));
      setAvailableLeagues(leagues);
    }
  }, [groupedMatches]);

  // Generate date options
  const getDateOptions = () => {
    const options = [
      { value: 'all', label: '📅 All' },
      { value: 'today', label: 'Today' },
      { value: 'tomorrow', label: 'Tomorrow' }
    ];
    
    const today = new Date();
    for (let i = 2; i <= 6; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      const displayStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      options.push({ value: dateStr, label: displayStr });
    }
    
    return options;
  };

  const dateOptions = getDateOptions();

  // Get live matches
  const getLiveStatuses = () => {
    if (selectedSport === 'football') {
      return ['1H', 'HT', '2H', 'ET', 'P'];
    } else if (selectedSport === 'basketball') {
      return ['1Q', '2Q', 'HT', '3Q', '4Q', 'OT'];
    }
    return [];
  };
  
  const liveMatches = displayedMatches.filter(m => getLiveStatuses().includes(m.status));

  const getSportIcon = () => {
    const sport = sports.find(s => s.id === selectedSport);
    return sport ? sport.icon : '🎯';
  };

  const getSportTitle = () => {
    return selectedSport.charAt(0).toUpperCase() + selectedSport.slice(1) + ' Matches';
  };

  const totalMatches = flattenMatches(groupedMatches).length;

  const isTopLeague = (leagueId, leagueName) => {
    const priority = getLeaguePriority(leagueId, leagueName, selectedSport);
    return priority <= 7;
  };

  const isWorldCup = (leagueId, leagueName) => {
    if (selectedSport === 'football') {
      return leagueId === 1 || leagueName?.toLowerCase().includes('world cup');
    }
    return false;
  };

  const isWNBA = (leagueId, leagueName) => {
    if (selectedSport === 'basketball') {
      return leagueId === 13 || leagueName?.toLowerCase().includes('wnba') || leagueName?.toLowerCase().includes('nba w');
    }
    return false;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsFilterOpen(false);
  };

  const toggleFilterMenu = () => {
    setIsFilterOpen(!isFilterOpen);
    setIsMobileMenuOpen(false);
  };

  // Mobile Sport Selector Component
  const SportSelector = () => (
    <div className="lg:hidden mb-4">
      <div
        ref={scrollContainerRef}
        className="flex gap-2 overflow-x-auto scroll-smooth hide-scrollbar pb-2 px-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {sports.map((sport) => (
          <button
            key={sport.id}
            onClick={() => {
              setSelectedSport(sport.id);
              setSelectedLeague(null);
            }}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap transition-all duration-200 flex-shrink-0
              ${selectedSport === sport.id 
                ? `bg-gradient-to-r ${sport.color} text-white shadow-lg scale-105` 
                : 'bg-gray-800/70 text-gray-400 hover:bg-gray-700 hover:text-white'
              }
            `}
          >
            <span className="text-lg">{sport.icon}</span>
            <span className="font-medium text-sm">{sport.name}</span>
            {selectedSport === sport.id && (
              <span className="ml-1 w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
        {/* Mobile Sport Selector - Only visible on mobile */}
        <SportSelector />

        {/* Page Header - Desktop only */}
        <div className="hidden lg:flex lg:justify-between lg:items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {getSportIcon()} Sports Betting
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                value={activeDateFilter}
                onChange={(e) => handleDateFilter(e.target.value)}
                className="bg-gray-800 text-white px-3 py-2 rounded-lg text-sm appearance-none cursor-pointer pr-8 min-w-[140px]"
              >
                {dateOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <FiCalendar className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
            </div>

            <Link to="/betslip" className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold text-sm">
              Betslip ({betslipCount})
            </Link>
          </div>
        </div>

        {/* Mobile Sidebar Overlay - For sport selection */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleMobileMenu}>
            <div className="absolute left-0 top-0 h-full w-64 bg-gray-900 shadow-lg" onClick={(e) => e.stopPropagation()}>
              <div className="p-4">
                <h3 className="text-lg font-bold text-white mb-4">Sports Categories</h3>
                <SportsSidebar 
                  selectedSport={selectedSport}
                  onSportSelect={(sport) => {
                    setSelectedSport(sport);
                    setSelectedLeague(null);
                    setIsMobileMenuOpen(false);
                  }}
                  selectedDate={selectedDate}
                  onLeagueSelect={handleLeagueSelect}
                  fixtures={groupedMatches}
                  isMobile={true}
                  onClose={toggleMobileMenu}
                />
              </div>
            </div>
          </div>
        )}

        {/* Mobile Filter Overlay */}
        {isFilterOpen && (
          <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleFilterMenu}>
            <div className="absolute right-0 top-0 h-full w-64 bg-gray-900 shadow-lg p-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white mb-4">Filter Options</h3>
              <div className="space-y-4">
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">
                    Odds Format: <span className="text-yellow-400 font-bold">American</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Sidebar - Desktop only */}
          <div className="hidden lg:block w-full lg:w-64 flex-shrink-0">
            <SportsSidebar 
              selectedSport={selectedSport}
              onSportSelect={setSelectedSport}
              selectedDate={selectedDate}
              onLeagueSelect={handleLeagueSelect}
              fixtures={groupedMatches}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Controls Bar - Mobile optimized with league filter */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {/* Date Filter */}
              <div className="relative flex-1 min-w-[100px] max-w-[160px]">
                <select
                  value={activeDateFilter}
                  onChange={(e) => handleDateFilter(e.target.value)}
                  className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg text-xs sm:text-sm appearance-none cursor-pointer pr-7"
                >
                  {dateOptions.slice(0, 4).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <FiCalendar className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={12} />
              </div>

              {/* League Filter Button - Mobile dropdown like date filter */}
              {availableLeagues.length > 1 && (
                <div className="relative">
                  <button
                    onClick={() => setShowLeagueFilter(!showLeagueFilter)}
                    className="bg-gray-800 text-white px-3 py-2 rounded-lg text-xs sm:text-sm flex items-center gap-1"
                  >
                    <FiFilter className="w-3 h-3" />
                    {selectedLeague ? '1 League' : 'All Leagues'}
                    <FiChevronDown className={`w-3 h-3 transition-transform ${showLeagueFilter ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showLeagueFilter && (
                    <div className="absolute top-full left-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg z-20 min-w-[180px] max-h-60 overflow-y-auto">
                      <button
                        onClick={() => handleLeagueSelect(null)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-800 ${!selectedLeague ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                      >
                        All Leagues
                      </button>
                      {availableLeagues.map(league => (
                        <button
                          key={league.id}
                          onClick={() => handleLeagueSelect(league.id)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-800 flex items-center gap-2 ${selectedLeague === league.id ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                        >
                          {league.logo && <img src={league.logo} alt="" className="w-4 h-4 object-contain" />}
                          {league.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              
              {/* Betslip Button - Mobile */}
              <Link to="/betslip" className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1">
                <span>📋</span>
                <span className="hidden xs:inline">Betslip</span>
                <span className="bg-green-800 px-1.5 py-0.5 rounded-full text-xs">{betslipCount}</span>
              </Link>
            </div>

            {/* Stats Bar */}
            <div className="flex items-center gap-3 mb-4 text-xs text-gray-400 bg-gray-900/50 rounded-lg px-3 py-2">
              <span>{displayedMatches.length} of {totalMatches} matches</span>
              {liveMatches.length > 0 && (
                <span className="flex items-center gap-1 text-red-400">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                  {liveMatches.length} LIVE
                </span>
              )}
              {selectedLeague && (
                <span className="text-blue-400">• Filtered</span>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 bg-red-900/50 border border-red-700 rounded-lg p-3">
                <p className="text-red-200 text-xs">{error}</p>
              </div>
            )}

            {/* Main Content */}
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
                <p className="text-gray-400 mt-4 text-sm">Loading {selectedSport} matches...</p>
              </div>
            ) : displayedMatches.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-5xl mb-4">{getSportIcon()}</div>
                <h3 className="text-lg font-bold text-white mb-2">No matches available</h3>
                <p className="text-gray-400 text-sm mb-6">
                  {error || `No ${selectedSport} matches found for the selected filter.`}
                </p>
              
              </div>
            ) : (
              <div className="space-y-6">
                {(() => {
                  const groupedDisplay = {};
                  displayedMatches.forEach(match => {
                    const leagueId = match._league_id || match.league_id;
                    if (!groupedDisplay[leagueId]) {
                      groupedDisplay[leagueId] = {
                        league_id: leagueId,
                        league_name: match._league_name || match.league_name || `League ${leagueId}`,
                        league_logo: match._league_logo || match.league_logo,
                        country: match._league_country,
                        fixtures: []
                      };
                    }
                    groupedDisplay[leagueId].fixtures.push(match);
                  });
                  
                  return Object.values(groupedDisplay).map((group) => {
                    const isWnbaLeague = isWNBA(group.league_id, group.league_name);
                    const isWorldCupLeague = isWorldCup(group.league_id, group.league_name);
                    const isTop = isTopLeague(group.league_id, group.league_name);
                    
                    const getBadgeColor = () => {
                      if (selectedSport === 'basketball') {
                        if (isWnbaLeague) return 'bg-purple-900/50 text-purple-400 border-purple-700';
                        if (isTop) return 'bg-blue-900/50 text-blue-400 border-blue-700';
                        return 'bg-gray-800/50 text-gray-400 border-gray-700';
                      } else {
                        if (isWorldCupLeague) return 'bg-yellow-900/50 text-yellow-400 border-yellow-700';
                        if (isTop) return 'bg-blue-900/50 text-blue-400 border-blue-700';
                        return 'bg-gray-800/50 text-gray-400 border-gray-700';
                      }
                    };
                    
                    return (
                      <div key={group.league_id} className="space-y-3">
                        <div className={`flex items-center gap-2 rounded-lg p-2.5 sticky top-14 z-10 backdrop-blur-sm border ${getBadgeColor()}`}>
                          {group.league_logo && (
                            <img src={group.league_logo} alt={group.league_name} className="w-5 h-5 object-contain" />
                          )}
                          <h3 className="text-sm font-bold text-white truncate">{group.league_name}</h3>
                          {isWnbaLeague && (
                            <span className="text-[10px] bg-purple-700 text-white px-1.5 py-0.5 rounded-full flex-shrink-0">WNBA</span>
                          )}
                          {isWorldCupLeague && (
                            <span className="text-[10px] bg-yellow-700 text-white px-1.5 py-0.5 rounded-full flex-shrink-0">🏆</span>
                          )}
                          <span className="text-[10px] text-gray-400 bg-gray-700 px-1.5 py-0.5 rounded ml-auto flex-shrink-0">
                            {group.fixtures.length}
                          </span>
                        </div>
                        
                        <div className={viewMode === 'grid' 
                          ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3" 
                          : "space-y-3"
                        }>
                          {group.fixtures.map(match => (
                            <div key={match.id} onClick={() => handleMatchClick(match.id)} className="cursor-pointer">
                              <MatchCard
                                match={match}
                                sport={selectedSport}
                                oddsFormat="american"
                                viewMode={viewMode}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
                
                {/* Load More */}
                {hasMore && (
                  <div ref={loadMoreRef} className="flex justify-center py-4">
                    {loadingMore ? (
                      <div className="flex items-center gap-3 text-gray-400 text-sm">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
                        <span>Loading...</span>
                      </div>
                    ) : (
                      <button
                        onClick={loadMoreMatches}
                        className="bg-gray-800 hover:bg-gray-700 text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
                      >
                        <FiChevronDown className="w-4 h-4" />
                        Load More
                        <span className="text-xs text-gray-400">({displayedMatches.length}/{totalMatches})</span>
                      </button>
                    )}
                  </div>
                )}
                
                {!hasMore && totalMatches > 20 && (
                  <div className="text-center py-2 text-gray-500 text-xs border-t border-gray-800">
                    ✓ All {totalMatches} matches loaded
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Quick Navigation */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 p-3 z-30">
          <div className="flex justify-around">
            <button onClick={toggleMobileMenu} className="flex flex-col items-center text-gray-400 hover:text-white">
              <FiMenu className="w-5 h-5 mb-1" />
              <span className="text-xs">Sports</span>
            </button>
            <Link to="/betslip" className="flex flex-col items-center text-gray-400 hover:text-white">
              <div className="relative">
                <span className="text-lg">📋</span>
                {betslipCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {betslipCount}
                  </span>
                )}
              </div>
              <span className="text-xs">Betslip</span>
            </Link>
            <button onClick={toggleFilterMenu} className="flex flex-col items-center text-gray-400 hover:text-white">
              <FiFilter className="w-5 h-5 mb-1" />
              <span className="text-xs">Filter</span>
            </button>
            <Link to="/live" className="flex flex-col items-center text-gray-400 hover:text-white">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mb-1"></div>
              <span className="text-xs">Live</span>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SportsPage;